import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";

import { printZplLabel } from "./shippingHubApi";
import type { LabelImage, ZplPrinter } from "./shippingHubTypes";

type AddressBookRow = {
  kind: "customer" | "supplier" | "contact";
  source_id: string;
  company_name: string | null;
  attention: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country_code: string | null;
};

const KIND_LABEL: Record<AddressBookRow["kind"], string> = {
  customer: "Customer",
  supplier: "Supplier",
  contact: "Contact",
};

const fieldClass =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900";

export function OneOffUpsPage() {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<AddressBookRow[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [company, setCompany] = useState("");
  const [attention, setAttention] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");
  const [serviceCode, setServiceCode] = useState("03");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tracking, setTracking] = useState<string | null>(null);
  const [labels, setLabels] = useState<LabelImage[]>([]);
  const [printers, setPrinters] = useState<ZplPrinter[]>([]);
  const [printer, setPrinter] = useState("");
  const [printMessage, setPrintMessage] = useState<string | null>(null);

  async function search() {
    setSearchError(null);
    try {
      const data = await apiFetch<{ rows: AddressBookRow[] }>(
        `/api/shipping/address-book?q=${encodeURIComponent(query.trim())}`,
      );
      setRows(data.rows);
    } catch (caught) {
      setSearchError(caught instanceof ApiError ? caught.message : String(caught));
    }
  }

  function applyRow(row: AddressBookRow) {
    setCompany(row.company_name ?? "");
    setAttention(row.attention ?? "");
    setLine1(row.address_line_1 ?? "");
    setLine2(row.address_line_2 ?? "");
    setCity(row.city ?? "");
    setState(row.state ?? "");
    setPostal(row.postal_code ?? "");
    setCountry((row.country_code ?? "").trim() || "US");
  }

  async function createLabel() {
    setSubmitting(true);
    setError(null);
    setPrintMessage(null);
    try {
      const data = await apiFetch<{ tracking: string; labels: LabelImage[] }>("/api/shipping/one-off-ups", {
        method: "POST",
        body: {
          company_name: company,
          attention,
          address_line_1: line1,
          address_line_2: line2,
          city,
          state,
          postal_code: postal,
          country_code: country,
          service_code: serviceCode,
          packages: [
            {
              length: Number(length),
              width: Number(width),
              height: Number(height),
              weight: Number(weight),
            },
          ],
        },
      });
      setTracking(data.tracking);
      setLabels(data.labels);
      const printerData = await apiFetch<{ printers: ZplPrinter[] }>("/api/shipping/zpl-printers");
      setPrinters(printerData.printers);
      setPrinter(printerData.printers[0]?.name ?? "");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : String(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function print() {
    const trackingNo = tracking?.split("/")[0];
    if (!trackingNo || !printer) return;
    setPrintMessage(null);
    try {
      await printZplLabel(trackingNo, printer);
      setPrintMessage("Label sent to printer.");
    } catch (caught) {
      setPrintMessage(caught instanceof ApiError ? caught.message : String(caught));
    }
  }

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Look up a customer, supplier, or contact that already has an address, or type an address. This creates a UPS label that is not tied to a sales shipment or a transfer.">
        One-off label
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Address book</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex gap-2">
            <input
              className={fieldClass}
              value={query}
              placeholder="Name, city, or email"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void search();
              }}
            />
            <Button type="button" variant="secondary" onClick={() => void search()}>
              Search
            </Button>
          </div>
          {searchError ? <p className="text-sm text-red-700">{searchError}</p> : null}
          <ul className="divide-y divide-slate-200">
            {rows.map((row) => (
              <li key={`${row.kind}-${row.source_id}`}>
                <button
                  type="button"
                  className="w-full px-2 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => applyRow(row)}
                >
                  <span className="font-medium text-slate-900">{row.company_name}</span>
                  <span className="ml-2 text-slate-500">{KIND_LABEL[row.kind]}</span>
                  <span className="block text-slate-600">
                    {[row.attention, row.address_line_1, row.city, row.state, row.postal_code]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ship to</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Company
            <input className={fieldClass} value={company} onChange={(event) => setCompany(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Attention
            <input className={fieldClass} value={attention} onChange={(event) => setAttention(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700 md:col-span-2">
            Street
            <input className={fieldClass} value={line1} onChange={(event) => setLine1(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700 md:col-span-2">
            Line 2
            <input className={fieldClass} value={line2} onChange={(event) => setLine2(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            City
            <input className={fieldClass} value={city} onChange={(event) => setCity(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            State
            <input className={fieldClass} value={state} onChange={(event) => setState(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Postal code
            <input className={fieldClass} value={postal} onChange={(event) => setPostal(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Country
            <input className={fieldClass} value={country} onChange={(event) => setCountry(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Service
            <select className={fieldClass} value={serviceCode} onChange={(event) => setServiceCode(event.target.value)}>
              <option value="03">Ground</option>
              <option value="12">3 Day Select</option>
              <option value="02">2nd Day Air</option>
              <option value="01">Next Day Air</option>
            </select>
          </label>
          <label className="text-sm text-slate-700">
            Weight (lb)
            <input className={fieldClass} inputMode="decimal" value={weight} onChange={(event) => setWeight(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Length (in)
            <input className={fieldClass} inputMode="decimal" value={length} onChange={(event) => setLength(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Width (in)
            <input className={fieldClass} inputMode="decimal" value={width} onChange={(event) => setWidth(event.target.value)} />
          </label>
          <label className="text-sm text-slate-700">
            Height (in)
            <input className={fieldClass} inputMode="decimal" value={height} onChange={(event) => setHeight(event.target.value)} />
          </label>
          <div className="md:col-span-2">
            <Button type="button" disabled={submitting} onClick={() => void createLabel()}>
              {submitting ? "Creating…" : "Create UPS label"}
            </Button>
            {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
            {tracking ? <p className="mt-2 text-sm text-slate-800">Tracking {tracking}</p> : null}
            {labels.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select className={fieldClass} value={printer} onChange={(event) => setPrinter(event.target.value)}>
                  {printers.map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <Button type="button" variant="secondary" onClick={() => void print()}>
                  Print
                </Button>
                {printMessage ? <span className="text-sm text-slate-600">{printMessage}</span> : null}
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
